import React, { useEffect, useState } from "react";
import "i18next";
import { useTranslation } from "react-i18next";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { PrimeReactProvider, locale, addLocale } from "primereact/api";
import PropTypes from "prop-types";
import { Typography } from "@mui/material";
import { InputText } from "primereact/inputtext";

export default function PrimeReactDataGrid({
  enableGlobalFilter = true,
  enableStickyHeader = false,
  enableScrollable = false,
  scrollHeight = "500px",
  columns,
  filterprops,

  customtitle,
  ...props
}) {
  const { t, i18n } = useTranslation(["PrimeReactDataGrid"], {
    useSuspense: false,
  });
  const [filters, setFilters] = useState(null);
  const [globalFilterValue, setGlobalFilterValue] = useState("");

  useEffect(() => {
    const translatedLocale = {
      addRule: t("primereact.filter.add_rule"),
      removeRule: t("primereact.filter.remove_rule"),
      apply: t("primereact.filter.apply"),
      clear: t("primereact.filter.clear"),
    };

    addLocale(i18n.language, translatedLocale);
    locale(i18n.language);
  }, [i18n.language, t]);

  const initFilters = React.useCallback(() => {
    const updatedFilters = { ...filterprops };
    if (!enableGlobalFilter) {
      delete updatedFilters["global"];
    } else {
      updatedFilters["global"] = { value: null, matchMode: "contains" };
    }
    setFilters(updatedFilters);
  }, [filterprops, enableGlobalFilter]);

  useEffect(() => {
    initFilters();
  }, [initFilters]);

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    setGlobalFilterValue(value);

    if (enableGlobalFilter) {
      let _filters = { ...filters };
      _filters["global"].value = value;
      setFilters(_filters);
    }
  };

  const renderHeader = () => {
    if (!enableGlobalFilter) return null; // Hide global search input when disabled

    return customtitle ? (
      <div className="flex flex-wrap gap-2 justify-content-between align-items-center">
        <Typography
          variant="h4"
          component="h4"
          className="m-0"
          sx={{ fontWeight: "bold" }}
        >
          {customtitle}
        </Typography>
        <InputText
          value={globalFilterValue}
          onChange={onGlobalFilterChange}
          placeholder="Global Search"
        />
      </div>
    ) : (
      <InputText
        value={globalFilterValue}
        onChange={onGlobalFilterChange}
        placeholder="Global Search"
      />
    );
  };

  const primeReactNonce = {
    nonce: window.__CSP_NONCE__,
  };

  return (
    <div id="primeReactStyles" className="primeReactWrapper">
      <PrimeReactProvider value={primeReactNonce}>
        <div className="card">
          <DataTable
            {...props}
            header={enableGlobalFilter ? renderHeader() : null}
            filters={filters}
            {...(enableScrollable ? { scrollable: true, scrollHeight } : {})}
            className={enableStickyHeader ? "sticky-header-table" : ""}
          >
            {columns.map((col, index) => (
              <Column key={col?.field || index} {...col} />
            ))}
          </DataTable>
        </div>
      </PrimeReactProvider>
    </div>
  );
}

PrimeReactDataGrid.propTypes = {
  enableGlobalFilter: PropTypes.bool,
  enableStickyHeader: PropTypes.bool,
  scrollHeight: PropTypes.string,
  enableScrollable: PropTypes.bool,
  columns: PropTypes.array.isRequired,
  filterprops: PropTypes.object,
  customtitle: PropTypes.string,
};
